import type { StepUpGuardState } from '../hooks/use-biometric-guard'
import { LockIcon } from './lock-icon'

/**
 * Overlay shown while `useBiometricGuard` is confirming (or failed to
 * confirm) a step-up biometric check ahead of a sensitive account action.
 * Renders nothing while idle, so it's safe to mount unconditionally next to
 * the form it's guarding.
 */
export function StepUpConfirmModal({
  state,
  onRetry,
  onDismiss,
}: {
  state: StepUpGuardState
  onRetry: () => void
  onDismiss: () => void
}) {
  if (state.status === 'idle') return null

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Biometric confirmation"
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
        {state.status === 'checking' ? (
          <>
            <div
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600"
              aria-hidden
            >
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600" />
            </div>
            <p className="mt-4 text-sm font-medium text-slate-700" role="status" aria-live="polite">
              Confirm with Face ID / Touch ID to continue&hellip;
            </p>
          </>
        ) : (
          <>
            <div
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600"
              aria-hidden
            >
              <LockIcon className="h-6 w-6" />
            </div>
            <h2 className="mt-4 text-base font-semibold text-slate-900">Verification failed</h2>
            <p className="mt-1 text-sm text-slate-500" role="alert">
              {state.message}
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={onDismiss}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onRetry}
                autoFocus
                className="flex-1 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
              >
                Try again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
